import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCategories, getVideosByCategory } from '@/utils/video';

export default function AboutPage() {
  const categories = getCategories();

  useEffect(() => {
    document.title = '关于我们 - 宅男视频';
  }, []);

  const steps = [
    {
      icon: 'ri-compass-3-line',
      title: '挑选频道',
      text: '从首页或顶部分类菜单进入动画、游戏、影视片段三大频道，快速定位兴趣所在。',
    },
    {
      icon: 'ri-filter-3-line',
      title: '筛选排序',
      text: '在分类页按"最新发布"或"最多播放"排序，用最省时的方式找到想看的内容。',
    },
    {
      icon: 'ri-play-circle-line',
      title: '点击观看',
      text: '进入详情页查看视频信息与简介，播放器为界面演示，用于展示完整的浏览体验。',
    },
    {
      icon: 'ri-search-eye-line',
      title: '搜索直达',
      text: '用搜索框输入标题、分类或标签关键词，跨频道找到你记得的那个片段。',
    },
  ];

  const values = [
    { icon: 'ri-flashlight-line', title: '内容丰富', text: '聚合动画、游戏、影视片段三大方向，持续更新优质短视频。' },
    { icon: 'ri-layout-grid-line', title: '分类清晰', text: '频道与标签双重组织，配合排序与筛选，信息层次一目了然。' },
    { icon: 'ri-cursor-line', title: '播放简便', text: '即点即看，减少等待与跳转，把时间留给内容本身。' },
  ];

  return (
    <div className="w-full">
      <section className="w-full bg-primary-900">
        <div className="w-full px-4 md:px-6 py-12 md:py-16">
          <nav className="flex items-center flex-wrap gap-2 text-xs text-primary-200 mb-5">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">关于我们</span>
          </nav>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-semibold mb-4">
            <i className="ri-emotion-happy-line"></i>
            宅男们的内容基地
          </span>
          <h1 className="text-2xl md:text-4xl font-bold text-background-50 leading-tight max-w-3xl">
            关于宅男视频
          </h1>
          <p className="mt-4 text-sm md:text-base text-primary-100 max-w-2xl leading-relaxed">
            宅男视频是一个聚合动画、游戏与影视片段短视频的影音娱乐平台，专注为喜爱二次元与娱乐内容的用户提供多样、快捷、轻松的观看体验。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
          <div className="lg:col-span-2">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">我们的定位与使命</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              我们相信，碎片时间也可以很精彩。宅男视频的目标用户，是喜爱动画、游戏、影视剪辑及相关娱乐内容的年轻群体，
              他们偏好高频次、短时长的观看方式，习惯在通勤、午休、睡前的碎片时间里"刷一会儿"。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              因此我们把"<strong className="font-semibold text-foreground-950">好找、好筛、好看</strong>"作为核心使命：
              用清晰的分类降低发现成本，用排序与筛选提升浏览效率，用简洁的播放页把注意力还给内容本身。
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {values.map((item) => (
                <div key={item.title} className="rounded-lg bg-background-50 border border-background-200 p-5">
                  <span className="flex items-center justify-center w-11 h-11 rounded-lg bg-primary-100 text-primary-700 mb-3">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="text-sm font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg bg-background-50 border border-background-200 p-5">
              <h2 className="text-base font-bold text-foreground-950 mb-4">内容频道</h2>
              <ul className="flex flex-col gap-3">
                {categories.map((cat) => (
                  <li key={cat.slug}>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 rounded-md p-3 bg-background-100 border border-background-200 hover:border-accent-300 transition-colors cursor-pointer"
                    >
                      <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-100 text-accent-700 shrink-0">
                        <i className={`${cat.icon} text-lg`}></i>
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-foreground-950">{cat.name}</span>
                        <span className="block text-xs text-foreground-500 mt-0.5">
                          共 {getVideosByCategory(cat.slug).length} 个视频
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 rounded-lg bg-primary-900 p-5">
              <h2 className="text-base font-bold text-background-50 mb-3">联系我们</h2>
              <ul className="flex flex-col gap-3 text-sm text-primary-100">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line text-accent-400"></i>
                  hello@zhainan-video.demo
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-weibo-fill text-accent-400"></i>
                  官方微博：@宅男视频
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line text-accent-400"></i>
                  内容更新：每日 10:00 - 22:00
                </li>
              </ul>
              <p className="mt-4 text-xs text-primary-300 leading-relaxed">
                以上联系方式为演示用静态信息，暂不提供表单提交。
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full bg-background-50 border-y border-background-200">
        <div className="w-full px-4 md:px-6 py-10 md:py-16">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950">使用指南</h2>
          <p className="mt-2 text-sm text-foreground-600">四步上手，轻松找到想看的内容</p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {steps.map((step, index) => (
              <div key={step.title} className="relative rounded-lg bg-background-100 border border-background-200 p-5">
                <span className="absolute top-4 right-4 text-2xl font-bold text-background-300">
                  0{index + 1}
                </span>
                <span className="flex items-center justify-center w-11 h-11 rounded-lg bg-accent-500 text-primary-950 mb-3">
                  <i className={`${step.icon} text-xl`}></i>
                </span>
                <h3 className="text-sm font-bold text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-16">
        <div className="rounded-lg bg-primary-900 px-6 py-10 md:px-12 md:py-14 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-background-50">准备好开始了吗？</h2>
          <p className="mt-3 text-sm text-primary-100 max-w-xl mx-auto leading-relaxed">
            从精选短视频到多样分类，宅男视频随时等你来逛。所有内容均为演示数据，用于展示完整的浏览与观看体验。
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 font-semibold text-sm whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-primary-300 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-800 transition-colors cursor-pointer"
            >
              <i className="ri-search-line"></i>
              开始搜索
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}